import { CircleLazyImage, ELoadingType, Loading } from '@bilibili-maoer/prelude'
import { api } from '@bilibili-maoer/web-utils'

import { useEffect, useMemo, useState } from 'react'
import { useMount } from 'react-use'

import { Stack } from '@mui/material'
import moment from 'moment'
import * as qs from 'qs'
import { match } from 'ts-pattern'

import { Button } from '~/core/components/button'
import { StyledRouterLink } from '~/core/components/link'
import { Title } from '~/core/components/title'
import { asMissEvanPagination, defaultPageSize } from '~/core/pagination'
import { TTime, initDateTimeRange } from '~/core/utils/date'
import { forceFormatNumberWith } from '~/core/utils/number'
import { DataBlock } from '~/features/guild/components/data-block'
import { DateRange } from '~/features/guild/components/date-range'
import { GuildGuardWithRole } from '~/features/guild/components/router-guard'
import { ECellSxSize, FormattedTable, headerCellSxDefaut } from '~/features/guild/components/table'
import { Order, TableColumn } from '~/features/guild/components/table/types'
import { DataBlocksTitle, GuildTitle, TableTitle } from '~/features/guild/components/title'
import { DateRangeWrapper, Form } from '~/features/guild/components/wrappers'
import { GUILD_MIDDLE_TITLE } from '~/features/guild/constants'
import { EIncomeSortableKeys, GuildIncomeItem, IQuerySearch } from '~/features/guild/income/types'
import { EGuildIncomeType, ListTableRow } from '~/features/guild/types'
import { Links } from '~/router/links'
import { GuildApi } from '~/services/fm/guild'
import { UserApi } from '~/services/fm/user'
import useAppSearchParam from '~/shared/navigation/use-app-search-params'
import { MilliSeconds } from '~/shared/types/base/units'
import { EGuildRole } from '~/shared/types/services/user'

const INCOME_DATA_BLOCKS = [
  { field: EIncomeSortableKeys.GiftIncome, title: '礼物总收益（元）' },
  { field: EIncomeSortableKeys.NobleIncome, title: '贵族开通总收益（元）' },
  { field: EIncomeSortableKeys.SuperfanIncome, title: '超粉总收益（元）' },
  { field: EIncomeSortableKeys.PlayIncome, title: '玩法总收益（元）' },
]
export const GuildIncome = () => {
  const { data: userGuildRole } = UserApi.getUserGuildRole.useQuery()

  // eslint-disable-next-line no-bitwise
  const isOwner = !!((userGuildRole?.role || 0) & EGuildRole.Owner)
  const [searchParams, setSearchParams] = useAppSearchParam()
  const time = initDateTimeRange({
    startTime: (moment(searchParams.get('start_date')).valueOf() as MilliSeconds) || undefined,
    endTime: (moment(searchParams.get('end_date')).valueOf() as MilliSeconds) || undefined,
  })
  const [timeRange, setTimeRange] = useState(time)
  const [guildIncomeMenu, setGuildIncomeMenu] = useState<Record<EIncomeSortableKeys, number> | null>(null)
  const querySearch = useMemo(
    () => ({
      start_date: searchParams.get('start_date') || moment(time.startTime).format('YYYY-MM-DD'),
      end_date: searchParams.get('end_date') || moment(time.endTime).format('YYYY-MM-DD'),
      search_agent: searchParams.get('search_agent') || undefined,
      search_creator: searchParams.get('search_creator') || undefined,
      search_creator_id: searchParams.get('search_creator_id') || undefined,
      p: parseInt(searchParams.get('p') || '1'),
      order: (searchParams.get('order') as Order) || 'desc',
      order_by: (searchParams.get('order_by') as EIncomeSortableKeys) || undefined,
      pagesize: parseInt(searchParams.get('pagesize') || `${defaultPageSize}`),
    }),
    [searchParams, time.endTime, time.startTime]
  )

  const [creatorId, setCreatorId] = useState(querySearch.search_creator_id)
  const [creatorName, setCreatorName] = useState(querySearch.search_creator)
  const [agentName, setAgentName] = useState(querySearch.search_agent)
  const sortEncode = ({ order, order_by }: { order: Order; order_by: EIncomeSortableKeys }) =>
    order && order_by ? `${order_by || ''}.${order || ''}` : undefined

  const guildIncomeParams = {
    start_date: querySearch.start_date,
    end_date: querySearch.end_date,
    search_agent: querySearch.search_agent,
    search_creator: querySearch.search_creator || querySearch.search_creator_id || undefined,
    p: querySearch.p,
    sort: sortEncode({ order: querySearch.order, order_by: querySearch.order_by }),
  }

  const exportUrl = `${Links.GUILD_GUILD_INCOME_EXPORT_API_URL}?${qs.stringify({
    start_date: querySearch?.start_date,
    end_date: querySearch?.end_date,
    search_creator: querySearch?.search_creator_id || querySearch?.search_creator,
    search_agent: querySearch?.search_agent,
  })}`

  const [getGuildIncomeMenu, incomeMenuData] = GuildApi.getGuildIncomeMenu.useLazyQuery()
  const { data: guildIncomeData } = GuildApi.getGuildIncome.useQuery(guildIncomeParams)
  const pagination = guildIncomeData?.pagination && asMissEvanPagination(guildIncomeData.pagination)

  useEffect(() => {
    if (incomeMenuData.data?.menu) {
      setGuildIncomeMenu(incomeMenuData?.data?.menu)
    }
  }, [incomeMenuData.data?.menu])
  useEffect(() => {
    // 接口仅在第一页返回 menu 字段，所以需要缓存起来以便翻页后使用
    // 缺少数据时需要请求第一页的数据来使用
    if (guildIncomeData?.menu) {
      setGuildIncomeMenu(guildIncomeData.menu)
    }
  }, [guildIncomeData?.menu])

  useMount(() => {
    if (querySearch.p !== 1) {
      getGuildIncomeMenu(guildIncomeParams)
    }
  })
  const handleGuildIncomeParamsChange = (changedParams: Partial<IQuerySearch<EIncomeSortableKeys>>) => {
    setSearchParams(qs.stringify({ ...querySearch, ...changedParams }))
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleGuildIncomeParamsChange({
      start_date: moment(timeRange.startTime).format('YYYY-MM-DD'),
      end_date: moment(timeRange.endTime).format('YYYY-MM-DD'),
      p: 1,
    })
  }

  const handleSortChange = (orderBy: EIncomeSortableKeys) => {
    handleGuildIncomeParamsChange({
      order_by: orderBy,
      order:
        (orderBy === querySearch.order_by && querySearch.order === 'desc') || querySearch.order_by === undefined
          ? 'asc'
          : 'desc',
    })
  }

  const columns: TableColumn<ListTableRow<Partial<GuildIncomeItem>>>[] = [
    {
      field: 'creator_id',
      headerTitle: '主播 ID',
      filter: {
        props: {
          label: '主播 ID',
          variant: 'standard',
          value: creatorId || '',
          onChange: (e) => setCreatorId(e.target.value),
          onBlur: () =>
            handleGuildIncomeParamsChange({ search_creator_id: creatorId ? parseInt(creatorId) : undefined }),
          onKeyDown: (e) => {
            if (e.key === 'Enter') {
              handleGuildIncomeParamsChange({ search_creator_id: creatorId ? parseInt(creatorId) : undefined })
            }
          },
          inputProps: {
            inputMode: 'numeric',
            pattern: '[0-9]*',
          },
        },
      },
    },
    {
      field: 'creator_iconurl',
      headerTitle: '主播头像',
      renderBodyCell: (item) => (
        <a href={`${api.MISSEVAN_SITE_MAIN}/${item.row.creator_id}`} target="_blank">
          <CircleLazyImage src={item.row.creator_iconurl} alt={item.row.creator_username} width={50} height={50} />
        </a>
      ),
    },
    {
      field: 'creator_username',
      headerTitle: '主播昵称',
      filter: {
        props: {
          label: '主播昵称',
          variant: 'standard',
          value: creatorName || '',
          onChange: (e) => setCreatorName(e.target.value),
          onBlur: () => handleGuildIncomeParamsChange({ search_creator: creatorName || undefined }),
          onKeyDown: (e) => {
            if (e.key === 'Enter') {
              handleGuildIncomeParamsChange({ search_creator: creatorName || undefined })
            }
          },
        },
      },
    },
    {
      field: 'agent_username',
      headerTitle: '经纪人',
      renderBodyCell: (item) => item.row.agent_username || '-',
      filter: isOwner
        ? {
            props: {
              label: '经纪人',
              variant: 'standard',
              value: agentName || '',
              onChange: (e) => setAgentName(e.target.value),
              onBlur: () => handleGuildIncomeParamsChange({ search_agent: agentName }),
              onKeyDown: (e) => {
                if (e.key === 'Enter') {
                  handleGuildIncomeParamsChange({ search_agent: agentName })
                }
              },
            },
          }
        : undefined,
    },
    // @ts-expect-error 每项总收益的 title
    ...INCOME_DATA_BLOCKS.map(
      (item) =>
        ({
          field: item.field,
          headerTitle: item.title,
          headerCellSx: headerCellSxDefaut[ECellSxSize.Wide],
          sorter: {
            order: querySearch.order,
            orderBy: querySearch.order_by,
            onSort: ({ column }) => handleSortChange(column.field as EIncomeSortableKeys),
          },
        } as TableColumn<ListTableRow<Partial<GuildIncomeItem>>>)
    ),
    {
      // @ts-expect-error 额外的操作选项
      field: 'detail',
      headerTitle: '操作',
      headerCellSx: headerCellSxDefaut[ECellSxSize.Normal],
      renderBodyCell: (item) => (
        <StyledRouterLink
          to={{
            pathname: `/guild/income/${item.row.creator_id}`,
            search: qs
              .stringify({
                start_date: guildIncomeParams?.start_date,
                end_date: guildIncomeParams?.end_date,
                type: EGuildIncomeType.Live,
              })
              .toString(),
          }}
          state={{
            iconurl: item.row.creator_iconurl,
            username: item.row.creator_username,
            roomId: item.row.creator_id,
          }}
        >
          查看流水
        </StyledRouterLink>
      ),
    },
  ]

  return (
    <GuildGuardWithRole requireOneOfRole={[EGuildRole.Owner, EGuildRole.Agent]}>
      {match(guildIncomeData)
        .with(undefined, () => <Loading type={ELoadingType.Cat} />)
        .otherwise(() => (
          <>
            <Title>{['公会收益', GUILD_MIDDLE_TITLE]}</Title>
            <Form onSubmit={handleFormSubmit}>
              <Stack spacing={4}>
                <DateRangeWrapper>
                  <DateRange time={timeRange} onTimeRangeChange={(updatedTime: TTime) => setTimeRange(updatedTime)} />
                  <Button variant="contained" onClick={handleFormSubmit}>
                    查询
                  </Button>
                </DateRangeWrapper>
                <Stack spacing={2}>
                  <DataBlocksTitle>
                    <GuildTitle title="主播数据概况" />
                  </DataBlocksTitle>
                  <div>
                    {INCOME_DATA_BLOCKS.map((block) => {
                      const fieldName = block.field as EIncomeSortableKeys
                      return (
                        <DataBlock
                          key={block.field}
                          title={block.title}
                          content={forceFormatNumberWith(guildIncomeMenu?.[fieldName])}
                        />
                      )
                    })}
                  </div>
                </Stack>
                <Stack spacing={2}>
                  <TableTitle>
                    <GuildTitle title="主播数据列表" sx={{ flex: '1' }} />
                    {!!guildIncomeData?.data.length && (
                      <a href={exportUrl} download target="_blank">
                        <Button variant="contained">导出</Button>
                      </a>
                    )}
                  </TableTitle>
                  <FormattedTable
                    columns={columns}
                    rows={guildIncomeData?.data as ListTableRow<Partial<GuildIncomeItem>>[]}
                    page={guildIncomeParams.p || 1}
                    count={pagination?.page_count || 1}
                    onChange={(e, page) => handleGuildIncomeParamsChange({ p: page })}
                  />
                </Stack>
              </Stack>
            </Form>
          </>
        ))}
    </GuildGuardWithRole>
  )
}
